import React, { useState, useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { Hash, Globe, DollarSign, ExternalLink, Clock, HelpCircle, X, Link, Settings, Loader2, Users, Smartphone, MessageCircle, LogOut, Gem, Wallet, Volume2, Bell } from 'lucide-react'
import useAppStore from '../store/useAppStore'
import useAppEnvironment from '../hooks/useAppEnvironment'
import apiService from '../services/api'
import { userApi } from '../services'
import { openSupport, logoutSupport } from '../services/supportService'
import { isPushSupported, getNotificationPermission, hasPushSubscription, subscribePushNotifications, unsubscribePushNotifications } from '../services/pushService'
import toast from 'react-hot-toast'
import audioService from '../services/audioService'
import { getAvailableWithdrawMethods } from '../features/withdraw'
import { LANGUAGE_OPTIONS } from '../i18n/config'
import { ProfileCustomization, useProfileStore } from '../features/profile'

const SettingsPage = () => {
  const { t, i18n } = useTranslation()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const { user, language, isLanguageLoading, changeLanguage, loadProfile } = useAppStore()
  const { isTelegramMiniApp, isWebBrowser } = useAppEnvironment()
  // Свой публичный профиль для секции «Настройки профиля» (видимость, цвета)
  const { loadProfile: loadOwnerProfile } = useProfileStore()
  const [tradeLink, setTradeLink] = useState('')
  const [showTradeLinkModal, setShowTradeLinkModal] = useState(false)
  const [showLanguageModal, setShowLanguageModal] = useState(false)
  const [showCurrencyModal, setShowCurrencyModal] = useState(false)
  const [isLoadingProfile, setIsLoadingProfile] = useState(true)
  const [isSavingTradeLink, setIsSavingTradeLink] = useState(false)
  const [tradeLinkChangeCost, setTradeLinkChangeCost] = useState(null)
  const [isConfirmingTradeLink, setIsConfirmingTradeLink] = useState(false)
  const [currencies, setCurrencies] = useState([])
  const [isLoadingCurrencies, setIsLoadingCurrencies] = useState(false)
  const [isUpdatingCurrency, setIsUpdatingCurrency] = useState(false)
  const [homeScreenStatus, setHomeScreenStatus] = useState('unknown')
  const [isSoundLoading, setIsSoundLoading] = useState(false)
  // Push-уведомления: в Telegram Mini App и без PushManager блок скрывается
  const [pushSupported] = useState(() => isPushSupported())
  const [pushPermission, setPushPermission] = useState('default')
  const [isPushEnabled, setIsPushEnabled] = useState(false)
  const [isPushLoading, setIsPushLoading] = useState(false)

  // Инициализация состояния push-подписки при монтировании
  useEffect(() => {
    if (!pushSupported) {
      return
    }

    setPushPermission(getNotificationPermission())
    hasPushSubscription().then(setIsPushEnabled)
  }, [pushSupported])

  // Загрузка своего публичного профиля для секции кастомизации при входе и смене языка.
  // Всегда тянем свежие данные при монтировании: store чистится при уходе
  // с /profile, а guard «if (!ownerProfile)» захватывал устаревший профиль
  // ещё до reset() и пропускал загрузку — секция пропадала.
  useEffect(() => {
    loadOwnerProfile()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [language])

  // Проверка статуса шортката при монтировании
  useEffect(() => {
    // Проверяем версию WebApp API (метод доступен с версии 8.0)
    const webAppVersion = window.Telegram?.WebApp?.version || '6.0'
    const [major] = webAppVersion.split('.').map(Number)
    
    if (major >= 8 && window.Telegram?.WebApp?.checkHomeScreenStatus) {
      try {
        window.Telegram.WebApp.checkHomeScreenStatus((status) => {
          setHomeScreenStatus(status)
        })
      } catch (error) {
        console.log('checkHomeScreenStatus не поддерживается:', error)
        setHomeScreenStatus('unsupported')
      }
    } else {
      // Для версий ниже 8.0 помечаем как неподдерживаемое
      setHomeScreenStatus('unsupported')
    }
  }, [])

  // Обработка добавления на главный экран
  const handleAddToHomeScreen = useRef(null)
  
  useEffect(() => {
    let isSubscribed = false
    
    handleAddToHomeScreen.current = () => {
      // Проверяем версию WebApp API
      const webAppVersion = window.Telegram?.WebApp?.version || '6.0'
      const [major] = webAppVersion.split('.').map(Number)
      
      if (major < 8) {
        toast.error(t('profile.homeScreenNotSupported'))
        return
      }
      
      if (window.Telegram?.WebApp?.addToHomeScreen) {
        try {
          window.Telegram.WebApp.addToHomeScreen()
          
          // Подписываемся на событие успешного добавления (только если еще не подписаны)
          if (!isSubscribed) {
            isSubscribed = true
            
            const handleHomeScreenAdded = () => {
              setHomeScreenStatus('added')
              toast.success(t('profile.addedToHomeScreen'))
              // Отписываемся сразу после первого срабатывания
              window.Telegram.WebApp.offEvent('homeScreenAdded', handleHomeScreenAdded)
              isSubscribed = false
            }
            
            window.Telegram.WebApp.onEvent('homeScreenAdded', handleHomeScreenAdded)
          }
        } catch (error) {
          console.error('Ошибка при добавлении на главный экран:', error)
          toast.error(t('profile.addToHomeScreenError'))
        }
      } else {
        toast.error(t('profile.homeScreenNotSupported'))
      }
    }
    
    return () => {
      isSubscribed = false
    }
  }, [t])

  // Обработка смены языка с API вызовом
  const handleLanguageChange = async (newLanguage) => {
    try {
      // Закрываем модалку сразу
      setShowLanguageModal(false)
      
      // Используем новый метод из store для смены языка
      await changeLanguage(newLanguage, i18n.changeLanguage)
      
      // Показываем уведомление об успешной смене языка
      toast.success(t('profile.languageChanged'))
    } catch (error) {
      // Показываем ошибку пользователю
      if (!error.isNotified && !error.isHandledAuthError) {
        toast.error(error.isBackendError ? error.message : t('profile.languageChangeError'))
      }
      console.error('Ошибка смены языка в Profile:', error)
    }
  }

  // Загрузка профиля при монтировании компонента
  useEffect(() => {
    const loadUserProfile = async () => {
      try {
        setIsLoadingProfile(true)
        
        // Используем метод из store для синхронизации языка
        const response = await loadProfile(i18n.changeLanguage)
        
        // Предзаполняем поле трейдлинка если он есть
        if (response && response.me && response.me.tradelink) {
          setTradeLink(response.me.tradelink)
        }
      } catch (error) {
        console.error('Ошибка загрузки профиля:', error)
        if (!error.isNotified && !error.isHandledAuthError) {
          toast.error(error.isBackendError ? error.message : t('profile.profileLoadError'))
        }
      } finally {
        setIsLoadingProfile(false)
      }
    }

    loadUserProfile()
  }, []) // Убираем loadProfile из зависимостей чтобы избежать циклических вызовов

  // Автооткрытие модалки трейдлинка при переходе из инвентаря
  useEffect(() => {
    const openTradeLink = searchParams.get('openTradeLink')
    if (openTradeLink === 'true' && user) {
      // Предзаполняем поле трейдлинка если он есть
      if (user.tradelink) {
        setTradeLink(user.tradelink)
      }
      setShowTradeLinkModal(true)
      
      // Очищаем URL параметр после открытия модалки
      const newSearchParams = new URLSearchParams(searchParams)
      newSearchParams.delete('openTradeLink')
      navigate(`/settings?${newSearchParams.toString()}`, { replace: true })
    }
  }, [user, searchParams])

  // Шаг 1: проверка и установка трейдлинка
  const handleSaveTradeLink = async () => {
    try {
      setIsSavingTradeLink(true)
      setTradeLinkChangeCost(null)
      
      // Шаг 1 — без confirm_change
      const response = await userApi.updateTradeUrl(tradeLink)
      
      // Успешно установлен бесплатно
      if (response.tradelink?.result) {
        setShowTradeLinkModal(false)
        toast.success(t('profile.tradeLinkSaved'))
        return
      }
      
      // Линк занят другим аккаунтом — нужна платная привязка
      if (response.error === 'not uniq tradelink') {
        setTradeLinkChangeCost(response.change_cost)
        return
      }
      
      // Любая другая ошибка
      if (response.error) {
        toast.error(response.error, { duration: 3000 })
      }
    } catch (error) {
      if (!error.isNotified && !error.isHandledAuthError) {
        toast.error(error.message || t('profile.tradeLinkError'), {
          duration: 3000
        })
      }
    } finally {
      setIsSavingTradeLink(false)
    }
  }
  
  // Шаг 2: подтверждение платной установки трейдлинка
  const handleConfirmTradeLink = async () => {
    try {
      setIsConfirmingTradeLink(true)
      
      // Шаг 2 — с confirm_change: 1
      const response = await userApi.confirmTradeUrl(tradeLink)
      
      // Успешно установлен (с списанием или бесплатно)
      if (response.tradelink?.result) {
        const charged = response.tradelink.charged
        if (charged > 0) {
          toast.success(t('profile.tradeLinkSavedCharged', { cost: charged }))
        } else {
          toast.success(t('profile.tradeLinkSaved'))
        }
        // Обновляем баланс
        await loadProfile()
        setShowTradeLinkModal(false)
        setTradeLinkChangeCost(null)
        return
      }
      
      // Недостаточно кристаллов
      if (response.error === 'not enough crystals') {
        toast.error(t('profile.tradeLinkNotEnoughCrystals', {
          cost: response.change_cost,
          balance: response.balance
        }), { duration: 4000 })
        // Ведём на пополнение
        navigate('/payment')
        return
      }
      
      // Любая другая ошибка
      if (response.error) {
        toast.error(response.error, { duration: 3000 })
      }
    } catch (error) {
      if (!error.isNotified && !error.isHandledAuthError) {
        toast.error(error.message || t('profile.tradeLinkError'), {
          duration: 3000
        })
      }
    } finally {
      setIsConfirmingTradeLink(false)
    }
  }

  // Обработка открытия модалки с предзаполнением
  const handleOpenTradeLinkModal = () => {
    // Если у пользователя уже есть трейдлинк, предзаполняем поле
    if (user?.tradelink) {
      setTradeLink(user.tradelink)
    }
    setTradeLinkChangeCost(null)
    setShowTradeLinkModal(true)
  }

  // Список поддерживаемых языков (названия на родном языке)
  const supportedLanguages = LANGUAGE_OPTIONS

  // Открытие модалки выбора языка
  const handleOpenLanguageModal = () => {
    setShowLanguageModal(true)
  }

  const handleOpenCurrencyModal = async () => {
    try {
      setShowCurrencyModal(true)
      setIsLoadingCurrencies(true)

      const response = await userApi.getCurrencies()
      setCurrencies(response)
    } catch (error) {
      if (!error.isNotified && !error.isHandledAuthError) {
        toast.error(error.isBackendError ? error.message : t('api.requestFailed'))
      }
    } finally {
      setIsLoadingCurrencies(false)
    }
  }

  const handleCurrencyChange = async (currencyCode) => {
    try {
      if (!currencyCode || isUpdatingCurrency) {
        return
      }

      const currentCurrencyCode = user?.currency?.code
      if (currentCurrencyCode === currencyCode) {
        setShowCurrencyModal(false)
        return
      }

      setIsUpdatingCurrency(true)
      await userApi.updateCurrency(currencyCode)
      await loadProfile(i18n.changeLanguage)

      setShowCurrencyModal(false)
    } catch (error) {
      if (!error.isNotified && !error.isHandledAuthError) {
        toast.error(error.isBackendError ? error.message : t('api.requestFailed'))
      }
    } finally {
      setIsUpdatingCurrency(false)
    }
  }

  const getCurrentLanguageName = () => {
    const currentLang = supportedLanguages.find(lang => lang.code === language)
    return currentLang ? currentLang.name : language
  }

  // Проверяем доступность метода addToHomeScreen
  const isHomeScreenAvailable = () => {
    const webAppVersion = window.Telegram?.WebApp?.version || '6.0'
    const [major] = webAppVersion.split('.').map(Number)
    return major >= 8 && window.Telegram?.WebApp?.addToHomeScreen
  }

  const getWebVersionLink = () => {
    if (!user?.web) {
      return null
    }

    const telegramInitData = window.Telegram?.WebApp?.initData

    if (telegramInitData && user.web.endsWith('#tgWebAppData=')) {
      return `${user.web}${encodeURIComponent(telegramInitData)}`
    }

    return user.web
  }

  const handleLogout = async () => {
    // Отзываем сессию чата поддержки, чтобы следующий игрок на устройстве не увидел переписку
    await logoutSupport()
    // Очищаем browser-auth состояние, включая cookie и сохраненные Telegram init params
    apiService.transitionToGuest()
    window.location.replace('/')
  }

  const handleOpenWebVersion = () => {
    const webVersionLink = getWebVersionLink()

    if (!webVersionLink) {
      return
    }

    apiService.get('/api/send_message/web_version/').catch((error) => {
      console.error('Ошибка отправки сообщения для веб-версии:', error)
    })

    if (window.Telegram?.WebApp?.openLink) {
      window.Telegram.WebApp.openLink(webVersionLink)
      return
    }

    window.open(webVersionLink, '_blank', 'noopener,noreferrer')
  }

  const handleOpenTelegramApp = () => {
    const telegramAppLink = 'https://t.me/skinozbot'

    if (window.Telegram?.WebApp?.openLink) {
      window.Telegram.WebApp.openLink(telegramAppLink)
      return
    }

    window.open(telegramAppLink, '_blank', 'noopener,noreferrer')
  }

  // Переключатель звука
  const handleSoundToggle = async () => {
    if (isSoundLoading) return

    const currentSound = user?.sound !== false
    const newSound = !currentSound

    // Отправляем явное желаемое состояние, не инвертируем на сервере
    setIsSoundLoading(true)
    try {
      const response = await userApi.updateSound(newSound)

      if (response.sound?.result) {
        // Обновляем UI по эху из ответа
        const enabled = response.sound.enabled
        audioService.setMuted(!enabled)
        // Обновляем user в store
        useAppStore.getState().setUser({ ...user, sound: enabled })
      }
    } catch (error) {
      if (!error.isNotified && !error.isHandledAuthError) {
        toast.error(error.isBackendError ? error.message : t('profile.soundUpdateError', 'Не удалось изменить настройку звука'))
      }
    } finally {
      setIsSoundLoading(false)
    }
  }

  // Переключатель push-уведомлений
  const handlePushToggle = async () => {
    if (isPushLoading) return

    // Отказ в системном диалоге необратим — остаётся только инструкция
    if (pushPermission === 'denied') {
      toast.error(t('profile.pushDeniedHint'), { duration: 5000 })
      return
    }

    setIsPushLoading(true)
    try {
      if (isPushEnabled) {
        const success = await unsubscribePushNotifications()
        if (success) {
          setIsPushEnabled(false)
          toast.success(t('profile.pushDisabled'))
        } else {
          toast.error(t('profile.pushError'))
        }
      } else {
        // Запрос разрешения происходит внутри обработчика клика — это обязательно
        const subscription = await subscribePushNotifications()
        if (subscription) {
          setIsPushEnabled(true)
          setPushPermission('granted')
          toast.success(t('profile.pushEnabled'))
        } else {
          setPushPermission(getNotificationPermission())
          toast.error(t('profile.pushError'))
        }
      }
    } finally {
      setIsPushLoading(false)
    }
  }

  // Копирование ID игрока (для саппорта)
  const handleCopyId = async () => {
    if (!user?.telegramid) return
    try {
      await navigator.clipboard.writeText(String(user.telegramid))
      toast.success(t('profile.idCopied', 'ID скопирован'))
    } catch (e) {
      toast.error(t('profile.idCopyError', 'Не удалось скопировать ID'))
    }
  }

  const profileSections = [
    {
      title: t('profile.settings'),
      items: [
        {
          icon: Hash,
          label: t('profile.playerId', 'ID игрока'),
          value: user?.telegramid ? String(user.telegramid) : null,
          onClick: user?.telegramid ? handleCopyId : null
        },
        {
          icon: Globe,
          label: t('profile.language'),
          value: isLanguageLoading ? t('profile.changing') || 'Изменяется...' : getCurrentLanguageName(),
          onClick: isLanguageLoading ? null : handleOpenLanguageModal,
          isLoading: isLanguageLoading
        },
        {
          icon: Link,
          label: t('profile.tradeLink'),
          value: user?.tradelink ? t('profile.change') : t('profile.setup'),
          onClick: handleOpenTradeLinkModal
        },
        {
          icon: DollarSign,
          label: user?.currency?.code || '---',
          value: user?.currency?.string || null,
          onClick: handleOpenCurrencyModal
        },
        {
          icon: Volume2,
          label: t('profile.sound', 'Звук'),
          value: user?.sound !== false ? t('profile.soundOn', 'Вкл') : t('profile.soundOff', 'Выкл'),
          onClick: handleSoundToggle,
          isLoading: isSoundLoading
        },
        // Push-уведомления — только в браузере/PWA с поддержкой Push API
        ...(pushSupported ? [{
          icon: Bell,
          label: t('profile.notifications', 'Уведомления'),
          value: isPushEnabled ? t('profile.soundOn', 'Вкл') : t('profile.soundOff', 'Выкл'),
          onClick: handlePushToggle,
          isLoading: isPushLoading
        }] : [])
      ]
    },
    {
      title: t('profile.additional'),
      items: [
        {
          icon: Users,
          label: t('referrals.title'),
          onClick: () => navigate('/referrals')
        },
        {
          icon: Clock,
          label: t('profile.transactions'),
          onClick: () => navigate('/transactions')
        },
        // Кнопка вывода кристаллов — показывается при доступности хотя бы одного метода вывода
        ...(getAvailableWithdrawMethods(user).length > 0 ? [{
          icon: Wallet,
          label: t('profile.withdraw', 'Вывод кристаллов'),
          onClick: () => navigate('/withdraw')
        }] : []),
        // Показываем кнопку только если метод доступен И еще не добавлено
        ...(isHomeScreenAvailable() && homeScreenStatus !== 'added' ? [{
          icon: Smartphone,
          label: t('profile.addToHomeScreen'),
          onClick: () => handleAddToHomeScreen.current?.()
        }] : []),
        // Показываем кнопку сообщества только если есть ссылка в профиле
        ...(user?.tg_channel ? [{
          icon: Users,
          label: t('profile.community'),
          onClick: () => window.open(user.tg_channel, '_blank')
        }] : []),
        // Показываем кнопку чата только если есть ссылка в профиле
        ...(user?.tg_chat ? [{
          icon: MessageCircle,
          label: t('profile.chat'),
          onClick: () => window.open(user.tg_chat, '_blank')
        }] : []),
        {
          icon: HelpCircle,
          label: t('profile.support'),
          onClick: openSupport
        }
      ]
    }
  ]

  return (
    <div className="text-white">
      <div className="mx-auto w-full max-w-xl lg:max-w-2xl p-4 lg:px-6 lg:py-8">
        {/* Заголовок страницы */}
        <h1 className="text-2xl font-bold text-accent-primary flex items-center gap-3 mb-6">
          <Settings size={24} />
          {t('profile.settings')}
        </h1>

        {isLoadingProfile && !user ? (
          <div className="py-12 flex items-center justify-center">
            <Loader2 size={28} className="text-accent-primary animate-spin" />
          </div>
        ) : (
        <div className="space-y-6">
        {profileSections.map((section, sectionIndex) => (
          <div key={sectionIndex} className="">
            <h2 className="text-lg font-semibold text-white mb-3 flex items-center gap-2">
              <Settings size={20} />
              {section.title}
            </h2>
            
            <div className="space-y-2">
              {section.items.map((item, itemIndex) => {
                const Icon = item.icon
                const isItemLoading = item.isLoading || false
                const isDisabled = !item.onClick || isItemLoading || item.disabled
                
                return (
                  <button
                    key={itemIndex}
                    onClick={isDisabled ? undefined : item.onClick}
                    disabled={isDisabled}
                    className={`w-full bg-surface rounded-lg p-4 border border-white border-opacity-10 transition-all text-left ${
                      isDisabled 
                        ? 'opacity-60 cursor-not-allowed' 
                        : 'hover:border-accent-primary/30'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        {isItemLoading ? (
                          <Loader2 size={20} className="text-accent-primary animate-spin" />
                        ) : (
                          <Icon size={20} className="text-accent-primary" />
                        )}
                        <span className="font-medium text-white">{item.label}</span>
                      </div>
                      {item.value && (
                        <div className="flex items-center gap-2">
                          <span className="text-text-secondary">{item.value}</span>
                          {isItemLoading && (
                            <Loader2 size={16} className="text-text-secondary animate-spin" />
                          )}
                        </div>
                      )}
                    </div>
                  </button>
                )
              })}
            </div>
          </div>
        ))}

        {/* Настройки профиля: видимость + цвета (свой публичный профиль) */}
        <ProfileCustomization />

        {isTelegramMiniApp && user?.web && (
          <button
            onClick={handleOpenWebVersion}
            className="mb-3 w-full rounded-lg border border-accent-primary/30 bg-accent-primary/10 px-4 py-4 text-left font-medium text-accent-primary transition-all hover:border-accent-primary/50 hover:bg-accent-primary/15"
          >
            <div className="flex items-center gap-3">
              <ExternalLink size={20} className="text-accent-primary" />
              <span>SKINOZ PRO</span>
            </div>
          </button>
        )}

        {isWebBrowser && (
          <button
            onClick={handleOpenTelegramApp}
            className="mb-3 w-full rounded-lg border border-accent-primary/30 bg-accent-primary/10 px-4 py-4 text-left font-medium text-accent-primary transition-all hover:border-accent-primary/50 hover:bg-accent-primary/15"
          >
            <div className="flex items-center gap-3">
              <ExternalLink size={20} className="text-accent-primary" />
              <span>SKINOZ Telegram App</span>
            </div>
          </button>
        )}

        {isWebBrowser && (
          <button
            onClick={handleLogout}
            className="w-full rounded-lg border border-red-500/30 bg-red-500/10 px-4 py-4 text-left font-medium text-red-400 transition-all hover:border-red-400/50 hover:bg-red-500/15"
          >
            <div className="flex items-center gap-3">
              <LogOut size={20} className="text-red-400" />
              <span>{t('profile.logout')}</span>
            </div>
          </button>
        )}
        </div>
        )}
      </div>

      {/* Trade Link Modal */}
      {showTradeLinkModal && (
        <div className="fixed inset-0 bg-black/90 flex items-center justify-center p-6 z-50">
          <div className="bg-surface rounded-lg p-6 w-full max-w-md border border-white border-opacity-10">
            <h3 className="text-xl font-bold text-white mb-4">
              {t('profile.updateTradeLink')}
            </h3>
            
            <div className="mb-4">
              <label className="block text-sm font-medium text-text-secondary mb-2">
                {t('profile.steamTradeLink')}
              </label>
              <input
                type="url"
                value={tradeLink}
                onChange={(e) => setTradeLink(e.target.value)}
                placeholder="https://steamcommunity.com/tradeoffer/new/..."
                className="w-full bg-dark-primary border border-white border-opacity-10 rounded-lg px-4 py-3 text-white focus:border-accent-primary focus:outline-none"
              />
              
              {/* Подсказка с ссылкой на Steam */}
              <div className="mt-3 p-3 bg-accent-primary/10 border border-accent-primary/20 rounded-lg">
                <p className="text-sm text-text-secondary mb-2">
                  {t('profile.tradeLinkHint')}
                </p>
                <a 
                  href="https://steamcommunity.com/id/me/tradeoffers/privacy#trade_offer_access_url"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 text-accent-primary hover:text-accent-secondary transition-colors text-sm font-medium"
                >
                  {t('profile.getSteamTradeLink')}
                  <ExternalLink size={14} />
                </a>
              </div>
            </div>
            
            {/* Важное предупреждение */}
            <div className="mb-4 p-3 bg-orange-900/20 border border-orange-500/30 rounded-lg">
              <p className="text-orange-400 text-sm font-medium">
                ⚠️ {t('profile.tradeLinkWarning')}
              </p>
            </div>

            {/* Блок подтверждения платной привязки (шаг 2) */}
            {tradeLinkChangeCost !== null && (
              <div className="mb-4 p-4 bg-orange-900/20 border border-orange-500/40 rounded-lg">
                <p className="text-orange-400 text-sm font-medium mb-3">
                  {t('profile.tradeLinkAlreadyInUse')}
                </p>
                <div className="flex flex-col gap-2">
                  <button
                    onClick={handleConfirmTradeLink}
                    disabled={isConfirmingTradeLink || (tradeLinkChangeCost > 0 && (user?.balance ?? 0) < tradeLinkChangeCost)}
                    className="w-full py-3 bg-gradient-to-r from-accent-primary to-accent-secondary text-dark-primary font-bold rounded-lg hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  >
                    {isConfirmingTradeLink ? (
                      <>
                        <Loader2 size={16} className="animate-spin" />
                        {t('profile.saving')}
                      </>
                    ) : tradeLinkChangeCost > 0 ? (
                      <>
                        <Gem size={16} />
                        {t('profile.tradeLinkBindFor')} {tradeLinkChangeCost}
                      </>
                    ) : (
                      t('profile.tradeLinkBindFree')
                    )}
                  </button>
                  <button
                    onClick={() => setTradeLinkChangeCost(null)}
                    disabled={isConfirmingTradeLink}
                    className="w-full py-3 bg-transparent border border-text-secondary text-text-secondary rounded-lg hover:bg-text-secondary/10 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {t('common.cancel')}
                  </button>
                </div>
              </div>
            )}

            {/* Обычные кнопки (шаг 1) — скрываются в режиме подтверждения */}
            {tradeLinkChangeCost === null && (
              <div className="flex gap-3">
                <button
                  onClick={() => setShowTradeLinkModal(false)}
                  disabled={isSavingTradeLink}
                  className="flex-1 py-3 bg-transparent border border-text-secondary text-text-secondary rounded-lg hover:bg-text-secondary/10 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {t('common.cancel')}
                </button>
                <button
                  onClick={handleSaveTradeLink}
                  disabled={isSavingTradeLink}
                  className="flex-1 py-3 bg-gradient-to-r from-accent-primary to-accent-secondary text-dark-primary font-bold rounded-lg hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {isSavingTradeLink ? (
                    <>
                      <Loader2 size={16} className="animate-spin" />
                      {t('profile.saving')}
                    </>
                  ) : (
                    t('common.save')
                  )}
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Language Selection Modal */}
      {showLanguageModal && (
        <div className="fixed inset-0 bg-black/90 flex items-center justify-center p-6 z-50">
          <div className="bg-surface rounded-lg p-6 w-full max-w-md border border-white border-opacity-10">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-bold text-white">
                {t('profile.language')}
              </h3>
              <button
                onClick={() => setShowLanguageModal(false)}
                className="text-text-secondary hover:text-white transition-colors"
              >
                <X size={24} />
              </button>
            </div>
            
            <div className="space-y-2 max-h-[60vh] overflow-y-auto">
              {supportedLanguages.map((lang) => {
                const isSelected = language === lang.code
                const isLoading = isLanguageLoading && isSelected
                
                return (
                  <button
                    key={lang.code}
                    onClick={() => handleLanguageChange(lang.code)}
                    disabled={isLanguageLoading}
                    className={`w-full p-4 rounded-lg border transition-all text-left ${
                      isSelected
                        ? 'bg-accent-primary/20 border-accent-primary text-white'
                        : 'bg-dark-primary border-white border-opacity-10 text-text-secondary hover:border-accent-primary/30 hover:text-white'
                    } disabled:opacity-50 disabled:cursor-not-allowed`}
                  >
                    <div className="flex items-center justify-between">
                      <span className="font-medium">{lang.name}</span>
                      {isLoading ? (
                        <Loader2 size={20} className="text-accent-primary animate-spin" />
                      ) : isSelected ? (
                        <div className="w-5 h-5 rounded-full bg-accent-primary flex items-center justify-center hidden">
                          <div className="w-2 h-2 rounded-full bg-dark-primary" />
                        </div>
                      ) : null}
                    </div>
                  </button>
                )
              })}
            </div>
          </div>
        </div>
      )}

      {showCurrencyModal && (
        <div className="fixed inset-0 bg-black/90 flex items-center justify-center p-6 z-50">
          <div className="bg-surface rounded-lg p-6 w-full max-w-md border border-white border-opacity-10">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-bold text-white">
                {user?.currency?.string || user?.currency?.code || '---'}
              </h3>
              <button
                onClick={() => setShowCurrencyModal(false)}
                className="text-text-secondary hover:text-white transition-colors"
                disabled={isUpdatingCurrency}
              >
                <X size={24} />
              </button>
            </div>

            {isLoadingCurrencies ? (
              <div className="py-8 flex items-center justify-center">
                <Loader2 size={24} className="text-accent-primary animate-spin" />
              </div>
            ) : (
              <div className="space-y-2 max-h-[60vh] overflow-y-auto">
                {currencies.map((currency) => {
                  const isSelected = currency.select === true || user?.currency?.code === currency.code
                  const isLoading = isUpdatingCurrency && isSelected

                  return (
                    <button
                      key={currency.code}
                      onClick={() => handleCurrencyChange(currency.code)}
                      disabled={isUpdatingCurrency}
                      className={`w-full p-4 rounded-lg border transition-all text-left ${
                        isSelected
                          ? 'bg-accent-primary/20 border-accent-primary text-white'
                          : 'bg-dark-primary border-white border-opacity-10 text-text-secondary hover:border-accent-primary/30 hover:text-white'
                      } disabled:opacity-50 disabled:cursor-not-allowed`}
                    >
                      <div className="flex items-center justify-between gap-4">
                        <span className="font-medium truncate">{currency.string || currency.code}</span>
                        {isLoading ? <Loader2 size={20} className="text-accent-primary animate-spin shrink-0" /> : null}
                      </div>
                    </button>
                  )
                })}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

export default SettingsPage
